import type { BikeModel } from '@/domain/geo';

/* The two models Vammo rents today. Everything else the station record still lists —
   Tinbot TS1, VMOTO VS1/VS2/F01/NEW TS — is retired stock that would only add noise to a
   page whose whole job is "can I swap here for my bike". */
export type SupportedModel = 'comfort' | 'cpx';

/* Coverage is what the map paints: one colour per station, so the three states have to be
   mutually exclusive. */
export type Coverage = SupportedModel | 'both';

/* Keyed by id, not by name: the stored names drift ("VMOTO CPX" in the payload,
   "I/VMOTO SUPER SOCO CPX" on the document) while the id is what the station record
   actually joins on.

   Comfort carries two ids because the model was seeded separately per environment — 18 in
   production, 45 in dev. Listing both keeps dev honest; keying on production's id alone
   made every dev station look like CPX-only, which is a silent wrong answer rather than a
   visible break. */
const MODEL_BY_ID = new Map<number, SupportedModel>([
  [7, 'cpx'],
  [18, 'comfort'],
  [45, 'comfort'],
]);

/* Comfort first, then CPX — a fixed order so chips and legend never reshuffle between
   stations. */
const ORDER: readonly SupportedModel[] = ['comfort', 'cpx'];

const LABEL = {
  comfort: 'Comfort',
  cpx: 'CPX',
} satisfies Record<SupportedModel, string>;

/* Design-system tokens from index.css: green-500 "Positive", blue-500 "Neutral",
   neon-pink. Not derived at runtime — the legend has to name the same three colours the
   map paints, so they are pinned in one place. */
const COVERAGE_COLOR = {
  both: '#66ba50',
  comfort: '#0ba4f9',
  cpx: '#ff6ed8',
} satisfies Record<Coverage, string>;

/* A station listing only retired models tells us nothing about the two we rent, and
   hiding it would be worse than showing it — it is a real station with real cabinets. So
   assume it serves both rather than inventing a restriction the lead would plan around.
   Same for an empty list, which is what a payload without the relation looks like. */
export const supportedModels = (models: readonly BikeModel[]): SupportedModel[] => {
  const known = new Set<SupportedModel>();
  for (const model of models) {
    const supported = MODEL_BY_ID.get(model.id);
    if (supported) {
      known.add(supported);
    }
  }
  return known.size === 0 ? [...ORDER] : ORDER.filter((model) => known.has(model));
};

export const coverageOf = (models: readonly BikeModel[]): Coverage => {
  const supported = supportedModels(models);
  return supported.length === ORDER.length ? 'both' : (supported[0] ?? 'both');
};

export const coverageColor = (coverage: Coverage): string => COVERAGE_COLOR[coverage];

export const modelLabel = (model: SupportedModel): string => LABEL[model];
